import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getMe } from '@api/selectors/auth';
import Avatar from '@base/Avatar/Avatar';
import Image from '@base/FutureImage';
import { emptyObject } from '@constants/empty';
import styled from '@emotion/styled';
import { Typography } from '@material-ui/core';
import { useIsMobile } from '@stores/AppCommonData';
import { getIsDarkModeOn } from '@stores/selectors/darkModeStatusSelectors';
import useDarkModeStatus from '@stores/useDarkModeStatus';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import Paper from 'aquilla/core/atoms/Paper';
import useRatingProgressInfo from '../RatingProgress/useRatingProgressInfo';
import useRatingInfo from '../useRatingInfo';
import { getRankInfoString, getUserLevel } from '../utils';

const Wrapper = styled(Paper)`
  height: 112px;
  padding: ${Spacings.SPACING_6B};
  padding-right: ${Spacings.SPACING_10B};
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid var(--color-divider);
  border-radius: 8px;
  ${mediaQuery.uptoMobile} {
    padding: 0;
    border: none;
    height: 80px;
    background: var(--color-base-0);
  }
`;

const AvatarWrapper = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  position: relative;
  margin-right: ${Spacings.SPACING_6B};

  ${mediaQuery.uptoMobile} {
    width: 80px;
    height: 80px;
    margin-right: 0;
  }
`;

const Flex = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const Name = styled.div`
  font-weight: ${Fonts.BOLD};
  font-size: ${Fonts.H4};
  line-height: 26px;

  ${mediaQuery.uptoMobile} {
    font-weight: ${Fonts.SEMIBOLD};
    font-size: ${Fonts.H5};
    line-height: 24px;
  }
`;

const UserContainer = styled.div`
  display: flex;
  width: 100%;

  ${mediaQuery.uptoMobile} {
    flex-direction: row-reverse;
    justify-content: space-between;
  }
`;

const Badge = styled.div`
  color: ${({ $color }) => $color || 'var(--color-text-secondary)'};
  align-self: start;
  font-weight: ${Fonts.SEMIBOLD};
  font-size: ${Fonts.H5};
  line-height: 24px;

  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.H6};
    line-height: 20px;
  }
`;

const Rank = styled.div`
  font-weight: ${Fonts.SEMIBOLD};
  font-size: ${Fonts.P1};
  line-height: 20px;
  color: ${({ $color }) => $color || 'var(--color-text-secondary)'};
  border-radius: ${Spacings.SPACING_12B};
  padding: ${Spacings.SPACING_6} ${Spacings.SPACING_3B} ${Spacings.SPACING_6}
    ${Spacings.SPACING_2B};
  height: 32px;
  background: ${({ $bgcolor }) => $bgcolor || 'var(--color-i-white)'};
  display: flex;
  align-items: center;
  border: ${({ $borderColor }) =>
    $borderColor ? `1px solid ${$borderColor}` : 'none'};

  ${mediaQuery.uptoMobile} {
    height: 24px;
    margin-top: ${Spacings.SPACING_2B};
    font-size: ${Fonts.P2};
    line-height: 12px;
  }
`;

const DetailsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 89%;
  ${mediaQuery.uptoMobile} {
    flex-direction: column;
    align-items: flex-start;
    width: 70%;
  }
`;

const InfoString = styled.div`
  margin-left: ${Spacings.SPACING_6};
`;

const BadgeImage = styled(Image)`
  position: absolute;
  bottom: 0;
  right: 0;
`;

const NoRating = styled(Typography)`
  font-weight: ${Fonts.SEMIBOLD};
  color: var(--color-text-secondary);
  line-height: 24px;
`;

const StyledImage = styled(Avatar)`
  width: 64px;
  height: 64px;
  border-radius: 50%;

  ${mediaQuery.uptoMobile} {
    width: 80px;
    height: 80px;
  }
`;

const BadgeWrapper = styled.div`
  width: 12px;
  height: 12px;
  display: flex;
  align-items: center;
`;

const RatingBadgeCard = () => {
  const { data } = useRatingProgressInfo();
  const me = useMyInfo(getMe) || emptyObject;
  const { firstName, lastName, avatar } = me ?? {};
  const { currentRating, rank: rankData } = data;
  const { rank, exam } = rankData ?? {};
  const { levels } = useRatingInfo();
  const level = getUserLevel(currentRating, levels);
  const { label = '', color, strokedBadgeImageV1 } = level ?? {};
  const rankStates = getRankInfoString(rank, exam);
  const isMobile = useIsMobile();
  const isDarkMode = useDarkModeStatus(getIsDarkModeOn);

  // TODO - add loading state

  if (!data) {
    return null;
  }

  const getNoRatingBadge = () => {
    return isDarkMode
      ? '/compete/no-rating-dark.svg'
      : '/compete/no-rating-light.svg';
  };

  return (
    <Wrapper>
      <UserContainer>
        <AvatarWrapper>
          {avatar && <StyledImage src={avatar} alt="user" />}

          <BadgeImage
            src={strokedBadgeImageV1 ?? getNoRatingBadge()}
            width={isMobile ? 32 : 20}
            height={isMobile ? 32 : 20}
          />
        </AvatarWrapper>

        <DetailsContainer>
          <Flex>
            <Name>
              {firstName} {lastName}
            </Name>
            {currentRating ? (
              <Badge $color={color}>
                {label} {currentRating && '•'} {currentRating}
              </Badge>
            ) : (
              <NoRating variant="p1">No rating yet</NoRating>
            )}
          </Flex>
          {currentRating && (
            <Rank
              $color={rankStates.textColor}
              $bgcolor={rankStates.bgColor}
              $borderColor={rankStates.borderColor}
            >
              <BadgeWrapper>
                <Image
                  src={rankStates.badgeImage}
                  width={12}
                  height={12}
                  alt="trophy"
                />
              </BadgeWrapper>

              <InfoString>{rankStates.infoString}</InfoString>
            </Rank>
          )}
        </DetailsContainer>
      </UserContainer>
    </Wrapper>
  );
};

export default RatingBadgeCard;
